import { mt, MutationData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import React, { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from 'v5/design-system/Dialog';
import { Button } from 'v5/design-system/Button';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { useControllableState } from 'v5/platform/react/useControllableState';

export interface ArchiveConfirmationDialogProps {
  /**
   * Whether the item is currently archived
   */
  isArchived: boolean;
  /**
   * Name of the entity type (e.g., "production", "callsheet")
   */
  entityName: string;
  /**
   * Callback when user confirms the archive/unarchive action.
   * Should return a Promise that resolves when the action is complete.
   */
  onConfirm: () => Promise<void>;
  /**
   * The trigger element that opens the dialog. Without one the host opens it
   * through `open` (a keyboard shortcut, a row's menu).
   */
  trigger?: ReactNode;
  /** Controls the dialog from outside; leave out to let the trigger open it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional custom title for archive action
   */
  archiveTitle?: string;
  /**
   * Optional custom description for archive action
   */
  archiveDescription?: string;
  /**
   * Optional custom title for unarchive action
   */
  unarchiveTitle?: string;
  /**
   * Optional custom description for unarchive action
   */
  unarchiveDescription?: string;
  /**
   * Optional confirm button label for archive action
   */
  archiveConfirmLabel?: string;
  /**
   * Optional confirm button label for unarchive action
   */
  unarchiveConfirmLabel?: string;
  /**
   * Optional external mutation state. If provided, this will be used instead of internal state.
   */
  mutation?: MutationData<unknown, unknown>;
}

interface ArchiveConfirmationDialogContentProps {
  isArchived: boolean;
  entityName: string;
  onConfirm: () => void;
  onClose: () => void;
  isDisabled: boolean;
  mutation: MutationData<unknown, unknown>;
  archiveTitle?: string;
  archiveDescription?: string;
  unarchiveTitle?: string;
  unarchiveDescription?: string;
  archiveConfirmLabel?: string;
  unarchiveConfirmLabel?: string;
}

function ArchiveConfirmationDialogContent({
  isArchived,
  entityName,
  onConfirm,
  onClose,
  isDisabled,
  mutation,
  archiveTitle,
  archiveDescription,
  unarchiveTitle,
  unarchiveDescription,
  archiveConfirmLabel,
  unarchiveConfirmLabel
}: ArchiveConfirmationDialogContentProps) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ArchiveConfirmationDialog'
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Don't handle shortcuts if typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // 'y' to confirm
      if (e.key === 'y' && !isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
        return;
      }

      // 'n' to cancel
      if (e.key === 'n') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
    },
    [isDisabled, onConfirm, onClose]
  );

  const title = isArchived
    ? (unarchiveTitle ?? t('unarchiveTitle', { entityName }))
    : (archiveTitle ?? t('archiveTitle', { entityName }));
  const description = isArchived
    ? (unarchiveDescription ?? t('unarchiveDescription', { entityName }))
    : (archiveDescription ?? t('archiveDescription', { entityName }));
  const confirmLabel = isArchived
    ? (unarchiveConfirmLabel ?? t('unarchiveConfirm'))
    : (archiveConfirmLabel ?? t('archiveConfirm'));

  return (
    <div onKeyDown={handleKeyDown}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <DialogDescription className="sr-only">
          {isArchived
            ? t('srDescriptionUnarchive', { entityName })
            : t('srDescriptionArchive', { entityName })}
        </DialogDescription>
        {description}
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="secondary" disabled={isDisabled}>
            {t('cancel')}
          </Button>
        </DialogClose>
        {mt
          .journey(mutation)
          .initially(
            <Button
              variant={isArchived ? 'primary' : 'danger'}
              onClick={onConfirm}
              disabled={isDisabled}
            >
              {confirmLabel}
            </Button>
          )
          .during(
            <Button variant={isArchived ? 'primary' : 'danger'} disabled={true}>
              <LoadingSpinner className="mr-2 h-4 w-4" />
              {t('confirmInProgress', { label: confirmLabel })}
            </Button>
          )
          .catch(() => (
            <Button
              variant={isArchived ? 'primary' : 'danger'}
              onClick={onConfirm}
            >
              {t('confirmRetry', { label: confirmLabel })}
            </Button>
          ))
          .done(() => null)}
      </DialogFooter>
    </div>
  );
}

export function ArchiveConfirmationDialog({
  isArchived,
  entityName,
  onConfirm,
  trigger,
  open: openProp,
  onOpenChange,
  archiveTitle,
  archiveDescription,
  unarchiveTitle,
  unarchiveDescription,
  archiveConfirmLabel,
  unarchiveConfirmLabel,
  mutation: externalMutation
}: ArchiveConfirmationDialogProps) {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    fallbackValue: false
  });
  const internalMutation = promiseState.useMutation(async () => {
    await onConfirm();
  });
  const mutation = externalMutation ?? internalMutation.state;

  const handleConfirm = useCallback(async () => {
    try {
      await internalMutation.track(undefined);
      // Close dialog on success
      setOpen(false);
      // Reset mutation state after closing
      internalMutation.reset();
    } catch (error) {
      // Error is handled by the mutation state, dialog stays open for retry
      console.error('Archive/unarchive error:', error);
    }
  }, [internalMutation]);

  const isLoading = mt.isInProgress(mutation);
  const isDisabled = isLoading;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <ArchiveConfirmationDialogContent
          isArchived={isArchived}
          entityName={entityName}
          onConfirm={handleConfirm}
          onClose={() => setOpen(false)}
          isDisabled={isDisabled}
          mutation={mutation}
          archiveTitle={archiveTitle}
          archiveDescription={archiveDescription}
          unarchiveTitle={unarchiveTitle}
          unarchiveDescription={unarchiveDescription}
          archiveConfirmLabel={archiveConfirmLabel}
          unarchiveConfirmLabel={unarchiveConfirmLabel}
        />
      </DialogContent>
    </Dialog>
  );
}
